import { useEffect, useState } from 'react';
import { sx } from '../../ui/designStyles';
import { Act, S } from './ui';

// The design's bottom sheet (T11609, hSheet*): every tap on Home opens one. What it is,
// what happens next, one primary action. The primary action is a real write, handed in
// by whoever opened the sheet; the sheet closes and the toast says what happened.

export interface SheetRow {
  k: string;
  v: string;
  /** Numbered steps carry a coloured number; plain facts carry none. */
  n?: string;
  markColor?: string;
}

export interface SheetSpec {
  kicker: string;
  title: string;
  body: string;
  glyph?: string;
  color?: string;
  stat?: [string, string, string?];
  rows?: SheetRow[];
  /** Placeholder of the text box, when the action takes words. */
  input?: string;
  primary: string;
  /** The write behind the primary button. Resolves the toast's sentence. */
  onPrimary: (text: string) => Promise<string | null>;
}

/** Numbered steps, as the design's steps(arr, c) makes them (blue unless told). */
export const steps = (arr: [string, string][], c = '#2F5C97'): SheetRow[] => arr.map((r, i) => ({ n: String(i + 1), k: r[0], v: r[1], markColor: c }));
/** Numbered rows in the sheet's own colour (an array row in the design). */
export const numbered = (arr: [string, string][], c: string): SheetRow[] => arr.map((r, i) => ({ n: String(i + 1), k: r[0], v: r[1], markColor: c }));
/** Plain facts (the design's {k, v} rows: no number, no mark). */
export const facts = (arr: [string, string][]): SheetRow[] => arr.map((r) => ({ k: r[0], v: r[1] }));

const markCss = (c: string) =>
  'display:inline-flex;align-items:center;justify-content:center;width:26px;height:26px;flex:0 0 26px;border-radius:50%;font-size:16px;font-weight:700;color:#FCFAF4;background:' + c + ';margin-top:1px;';

export function Sheet({ sheet, onClose, readOnly, reason }: { sheet: SheetSpec; onClose: (toast: string | null) => void; readOnly: boolean; reason: string | null }) {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const color = sheet.color ?? '#12503C';
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  const primary = async () => {
    if (busy) return;
    setBusy(true);
    try {
      onClose(await sheet.onPrimary(text));
    } catch (e) {
      onClose(`Not sent: ${(e as Error).message || 'the server did not answer'}.`);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div style={sx('position:fixed;inset:0;z-index:60;display:flex;align-items:flex-end;justify-content:center;background:rgba(34,39,46,.42);animation:riseIn .22s ease;')}>
      <a href="#" aria-label="Close" onClick={(e) => { e.preventDefault(); onClose(null); }} style={sx('position:absolute;inset:0;')} />
      <div role="dialog" aria-modal="true" aria-label={sheet.title} data-testid="home-sheet" style={sx('position:relative;box-sizing:border-box;width:100%;max-width:560px;max-height:82vh;overflow:auto;background:#FCFAF4;border-radius:22px 22px 0 0;padding:14px 20px 24px;box-shadow:0 -12px 40px rgba(34,39,46,.25);animation:sheetUp .34s cubic-bezier(.22,.61,.36,1) both;')}>
        <S css="display:block;width:40px;height:4px;border-radius:2px;background:rgba(34,39,46,.18);margin:0 auto 14px;" />
        <S css="display:flex;align-items:center;gap:12px;">
          <S css={'display:inline-flex;align-items:center;justify-content:center;width:44px;height:44px;flex:0 0 44px;border-radius:14px;font-size:20px;font-weight:700;color:#FCFAF4;background:' + color + ';'}>{sheet.glyph ?? '✓'}</S>
          <S css="flex:1 1 0;min-width:0;">
            <S css="display:block;font-size:14px;font-weight:700;letter-spacing:.1em;color:#54606C;">{sheet.kicker}</S>
            <S css="display:block;font-size:19px;font-weight:700;letter-spacing:-.02em;line-height:1.15;color:#22272E;text-wrap:pretty;">{sheet.title}</S>
          </S>
        </S>
        <S css="display:block;font-size:16px;color:#3C454F;line-height:1.5;margin-top:12px;text-wrap:pretty;">{sheet.body}</S>
        {sheet.stat && (
          <S css="display:flex;align-items:baseline;gap:8px;margin-top:14px;">
            <S css={'font-size:34px;font-weight:700;letter-spacing:-.03em;line-height:1;color:' + (sheet.stat[2] ?? '#22272E') + ';'}>{sheet.stat[0]}</S>
            <S css="font-size:16px;color:#54606C;">{sheet.stat[1]}</S>
          </S>
        )}
        {(sheet.rows ?? []).map((r, i) => (
          <S key={i} css="display:flex;align-items:flex-start;gap:12px;padding:10px 0;border-top:1px solid rgba(34,39,46,.08);">
            {r.n ? <S css={markCss(r.markColor ?? color)}>{r.n}</S> : <span />}
            <S css="flex:1 1 0;min-width:0;">
              <S css="display:block;font-size:16px;font-weight:600;color:#22272E;">{r.k}</S>
              <S css="display:block;font-size:16px;color:#54606C;margin-top:2px;line-height:1.4;text-wrap:pretty;">{r.v}</S>
            </S>
          </S>
        ))}
        {sheet.input && (
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={sheet.input}
            aria-label={sheet.input}
            rows={3}
            style={sx('box-sizing:border-box;width:100%;margin-top:14px;padding:12px 14px;border-radius:12px;border:1px solid rgba(34,39,46,.18);background:#FBF8F1;font-size:16px;color:#22272E;font-family:inherit;line-height:1.4;resize:none;')}
          />
        )}
        <S css="display:flex;gap:10px;margin-top:16px;flex-wrap:wrap;">
          <Act
            css={'flex:1 1 0;min-width:0;display:inline-flex;align-items:center;justify-content:center;height:52px;padding:0 18px;border-radius:14px;font-size:16px;font-weight:700;text-decoration:none;color:#FCFAF4;background:' + color + ';'}
            onClick={primary}
            disabled={readOnly || busy}
            reason={reason}
            testId="sheet-primary"
          >
            {sheet.primary}
          </Act>
          <Act css="flex:0 0 auto;display:inline-flex;align-items:center;justify-content:center;height:52px;padding:0 18px;border-radius:14px;font-size:16px;font-weight:700;text-decoration:none;color:#3C454F;background:rgba(34,39,46,.07);" onClick={() => onClose(null)}>
            Close
          </Act>
        </S>
      </div>
    </div>
  );
}
